import { useMemo } from 'react';
import clsx from 'clsx';

import { capitalize, css, styled } from '@mui/material';

import StatusIcon from './StatusIcon';
import { statusClasses } from './theme';

import type { Theme } from '../../types';
import type { StatusOwnerState, StatusProps, StatusType } from './types';

// theme.ts에서 이동한 헬퍼 함수
const getStatusColorToken = (
  theme: Theme,
): Record<Exclude<StatusType, 'determinate-progress' | 'indeterminate-progress'>, string> => ({
  active: theme.color['icon/success/default'],
  'active-informative': theme.color['icon/informative/default'],
  running: theme.color['icon/success/default'],
  'running-informative': theme.color['icon/informative/default'],
  'running-warning': theme.color['icon/warning/default'],
  error: theme.color['icon/danger/default'],
  'fatal-error': theme.color['icon/default'],
  pending: theme.color['icon/weaker/default'],
  inactive: theme.color['icon/weaker/default'],
  done: theme.color['icon/weaker/default'],
  information: theme.color['icon/informative/default'],
  warning: theme.color['icon/warning/default'],
  healthy: theme.color['icon/success/default'],
  unknown: theme.color['icon/disabled'],
});

const Status = (props: StatusProps) => {
  /* shouldForwardProp 방지: variant, status, showLabel, determinateOptions, indeterminateOptions, labelMap, textVariant */
  const {
    size = 'sm', // 기본값 설정
    variant = 'filled', // 기본값 설정
    textVariant = 'standalone', // 기본값 설정 (theme defaultProps에서 이동)
    status,
    hideLabel = false, // 기본값 설정
    labelMap,
    children,
    className,
    determinateOptions,
    indeterminateOptions,
    ...others
  } = props;

  const ownerState: StatusOwnerState = {
    ...props,
    size,
    variant,
    textVariant,
    hideLabel,
  };

  const getDynamicClasses = () => {
    return {
      root: clsx(
        size && statusClasses[`size${capitalize(size)}` as keyof typeof statusClasses],
        className,
      ),
    };
  };

  const dynamicClasses = getDynamicClasses();

  const label = useMemo(
    () => (!labelMap || !labelMap[status] ? children : labelMap[status]),
    [children, labelMap, status],
  );

  return (
    <StatusRoot ownerState={ownerState} className={dynamicClasses.root} {...others}>
      <StatusIconStyled
        status={status}
        variant={variant}
        size={size}
        determinateOptions={determinateOptions}
        indeterminateOptions={indeterminateOptions}
        ownerState={ownerState}
      />
      {!hideLabel && label && <StatusLabel ownerState={ownerState}>{label}</StatusLabel>}
    </StatusRoot>
  );
};

export default Status;

const StatusRoot = styled('div', {
  target: statusClasses.root,
})<{ ownerState: StatusOwnerState }>(({ ownerState, theme }) => {
  const { textVariant, size } = ownerState;

  return css`
    display: ${textVariant === 'inline' ? 'inline-flex' : 'flex'};
    align-items: center;
    gap: 4px;
    color: #131619;

    /* size variants */
    ${getSizeStyles(size, theme)}
  `;
});

const StatusIconStyled = styled(StatusIcon, {
  target: statusClasses.icon,
})<{ ownerState: StatusOwnerState }>(({ ownerState, theme }) => {
  const { status } = ownerState;
  const statusColorMap = getStatusColorToken(theme);

  return css`
    display: flex;
    align-items: center;
    ${status !== 'determinate-progress' && status !== 'indeterminate-progress'
      ? `color: ${statusColorMap[status]};`
      : ''}
  `;
});

const StatusLabel = styled('div', {
  target: statusClasses.label,
})<{ ownerState: StatusOwnerState }>(() => {
  return css`
    line-height: 1.5;
    font-weight: 400;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
  `;
});

// Style helper functions
const getSizeStyles = (size: StatusOwnerState['size'], theme: Theme) => {
  const sizeStylesMap = {
    xs: css`
      & .${statusClasses.label} {
        ${theme.typography['label/regular/xs']};
      }

      & .${statusClasses.icon} {
        width: 12px;
        height: 12px;
        font-size: 12px;
      }
    `,
    sm: css`
      & .${statusClasses.label} {
        ${theme.typography['label/regular/sm']};
      }

      & .${statusClasses.icon} {
        width: 16px;
        height: 16px;
        font-size: 16px;
      }
    `,
    md: css`
      & .${statusClasses.label} {
        ${theme.typography['label/regular/md']};
      }

      & .${statusClasses.icon} {
        width: 16px;
        height: 16px;
        font-size: 16px;
      }
    `,
    lg: css`
      gap: 6px;

      & .${statusClasses.label} {
        ${theme.typography['label/regular/lg']};
      }

      & .${statusClasses.icon} {
        width: 20px;
        height: 20px;
        font-size: 20px;
      }
    `,
    xl: css`
      gap: 6px;

      & .${statusClasses.label} {
        ${theme.typography['label/regular/xl']};
      }

      & .${statusClasses.icon} {
        width: 24px;
        height: 24px;
        font-size: 20px;
      }
    `,
  };

  return sizeStylesMap[size];
};
